import React, { useMemo } from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';
import CircleEffect from './effects/CircleEffect';
import WaveEffect from './effects/WaveEffect';
import PulseEffect from './effects/PulseEffect';
import ParticlesEffect from './effects/ParticlesEffect';
import GradientEffect from './effects/GradientEffect';
import RippleEffect from './effects/RippleEffect';

export type EffectType = 'none' | 'circle' | 'wave' | 'pulse' | 'particles' | 'gradient' | 'ripple';
export type TimerMode = 'countdown' | 'stopwatch';

export interface TimerEffectProps {
  effectType: EffectType;
  mode: TimerMode;
  totalMs: number;
  currentMs: number;
  showMilliseconds: boolean;
  size?: number;
  opacity?: number;
  color?: string;
  secondaryColor?: string;
  speed?: number;
  isRunning?: boolean;
}

const TimerEffect: React.FC<TimerEffectProps> = ({
  effectType = 'circle',
  mode = 'countdown',
  totalMs,
  currentMs,
  showMilliseconds = false,
  size = 160,
  opacity = 1,
  color = mode === 'countdown' ? '#4A6FA5' : '#FF6B6B',
  secondaryColor,
  speed = 1,
  isRunning = true
}) => {
  // 如果effectType为'none'，则不渲染任何内容
  if (effectType === 'none' || !isRunning) {
    return null;
  }

  // 计算进度(0-1)，根据是否显示毫秒和计时器模式采用不同的计算逻辑
  const progress = useMemo(() => {
    if (totalMs <= 0) return mode === 'countdown' ? 1 : 0;

    let calculatedProgress;
    
    if (mode === 'countdown') {
      // 倒计时模式：从1到0
      if (showMilliseconds) {
        // 显示毫秒时使用精确值
        calculatedProgress = Math.max(0, Math.min(1, currentMs / totalMs));
      } else {
        // 不显示毫秒时，模拟向上取整的效果
        const hoursPart = Math.floor(currentMs / (1000 * 60 * 60));
        const minutesPart = Math.floor((currentMs % (1000 * 60 * 60)) / (1000 * 60));
        const secondsPart = Math.ceil((currentMs % (1000 * 60)) / 1000);
        
        const displayTotalSeconds = hoursPart * 3600 + minutesPart * 60 + secondsPart;
        const totalSeconds = Math.ceil(totalMs / 1000);
        
        calculatedProgress = Math.max(0, Math.min(1, displayTotalSeconds / totalSeconds));
      }
    } else {
      // 秒表模式：从0到1（如果设置了总时间）或无限增长
      if (totalMs > 0) {
        calculatedProgress = Math.min(1, currentMs / totalMs);
      } else {
        // 如果没有设置总时间，使用时间本身作为进度的基础
        // 每小时循环一次完整的特效
        const oneHourMs = 60 * 60 * 1000;
        calculatedProgress = (currentMs % oneHourMs) / oneHourMs;
      }
    }

    return calculatedProgress;
  }, [totalMs, currentMs, showMilliseconds, mode]);

  // 根据不同的特效类型渲染对应的组件
  const renderEffect = () => {
    const commonProps = {
      progress,
      size,
      color,
      secondaryColor: secondaryColor || (mode === 'countdown' ? '#87A7D4' : '#FFAEAE'),
      opacity,
      speed,
      mode
    };

    switch (effectType) {
      case 'circle':
        return <CircleEffect {...commonProps} />;
      case 'wave':
        return <WaveEffect {...commonProps} />;
      case 'pulse':
        return <PulseEffect {...commonProps} />;
      case 'particles':
        return <ParticlesEffect {...commonProps} />;
      case 'gradient':
        return <GradientEffect {...commonProps} />;
      case 'ripple':
        return <RippleEffect {...commonProps} />;
      default:
        return null;
    }
  };

  return (
    <div className="timer-effect-container" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      {renderEffect()}
    </div>
  );
};

export default TimerEffect; 